import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

// INTERFACES
import { ICompany } from '../../../../client';

// SERVICES
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

@Injectable({
  providedIn: 'root',
})
export class ClientApiService {
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _uploading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _clients = new BehaviorSubject<ICompany>(<ICompany>{});
  private readonly _currentClient = new BehaviorSubject<ICompany | null>(
    <ICompany | null>null,
  );

  loading$ = this._loading.asObservable();
  clients$ = this._clients.asObservable();
  uploading$ = this._uploading.asObservable();
  currentClient$ = this._currentClient.asObservable();

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  get(params :{ [param :string] :string | string[] }) :Observable<any> {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV1}/clients`, { params })
      .pipe(
        tap({
          next: (response) => {
            return response;
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }

  createProfile(data :any) :Observable<any> {
    this._loading.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV1}/clients`,
        body: data,
      })
      .pipe(
        tap({
          next: (response) => {
            this._clients.next(response?.record);
            this._loading.next(false);
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }

  updateProfile(clientId :number, data :any) :Observable<any> {
    this._loading.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV1}/clients/${clientId}`,
        body: data,
      })
      .pipe(
        tap({
          next: (response) => {
            this._clients.next(response?.record);
            this._loading.next(false);
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }

  setDefaultDueDate(clientId :number, data :any) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/business-accounts/${clientId}/default-due-date`,
      body: data,
    });
  }

  upload(formData :FormData) :Observable<any> {
    this._uploading.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV1}/clients/upload`,
        contentType: 'multipart/form-data',
        body: formData,
      })
      .pipe(
        tap({
          next: (response) => {
            this._uploading.next(false);

            return response;
          },
          error: (error) => {
            this._uploading.next(false);

            return error;
          },
        }),
      );
  }

  setRecords(data :any) :void {
    this._clients.next(data);
  }

  getV2(
    params :{
      [param :string] :string | string[];
    } = {},
  ) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV2}/business-accounts`, {
      params,
    });
  }
}
